import { Component, OnInit } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  Engagements,
  GetDashboardsQuery,
  GetDashboardsQueryVariables,
  DashboardMetadata,
  DeleteChartOrDashboardInput,
  EntityType,
  DeleteChartsOrDashboardsMutation,
  DeleteChartsOrDashboardsMutationVariables,
  DeleteResponse,
} from '../../../../API';
import { GetDashboards } from '../../../../graphql/queries';
import { ApiService } from '../../../shared/services/api.service';
import { AppService } from '../../../shared/services/app.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DeleteChartsOrDashboards } from '../../../../graphql/mutations';
import { Router } from '@angular/router';
import { S3StorageService } from '../../../shared/services/s3-storage.service';

@Component({
  selector: 'app-manage-dashboards',
  templateUrl: './manage-dashboards.component.html',
  styleUrl: './manage-dashboards.component.scss',
})
export class ManageDashboardsComponent implements OnInit {
  engagement!: string;
  dashboards: DashboardMetadata[] = [];
  filteredDashboards: DashboardMetadata[] = [];
  isLoading: boolean = false;
  sortDescending: boolean = true;
  searchTerm: string = '';

  currentPage: number = 1;
  pageSize: number = 4;
  total: number = 0;
  spinnerContent: string = 'Loading Dashboards...';

  constructor(
    private appService: AppService,
    private apiService: ApiService,
    private nzMessageService: NzMessageService,
    private modalService: NzModalService,
    private router: Router,
    private s3Service: S3StorageService,
  ) {}

  ngOnInit(): void {
    this.appService.activeEngagement$.subscribe((engagement: Engagements) => {
      this.engagement = engagement.engagement;
      this.loadDashboards();
    });
  }

  private loadDashboards(): void {
    if (!this.engagement) return;

    this.isLoading = true;
    this.spinnerContent = 'Loading Dashboards...';
    this.apiService
      .query<GetDashboardsQuery, GetDashboardsQueryVariables>(GetDashboards, {
        engagementId: this.engagement,
      })
      .then((response) => {
        this.dashboards = (response.data?.GetDashboards as DashboardMetadata[]) || [];
        this.applyFiltersAndSort();
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to load charts: ${error}`);
        this.dashboards = [];
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private sortCharts(): void {
    this.filteredDashboards.sort((a, b) => {
      const dateA = new Date(a.lastModifiedAt || 0);
      const dateB = new Date(b.lastModifiedAt || 0);

      if (this.sortDescending) {
        return dateB.getTime() - dateA.getTime();
      } else {
        return dateA.getTime() - dateB.getTime();
      }
    });
  }

  private filterDashboards(): void {
    if (!this.searchTerm.trim()) {
      this.filteredDashboards = [...this.dashboards];
    } else {
      const searchLower = this.searchTerm.toLowerCase().trim();
      this.filteredDashboards = this.dashboards.filter((dashboard) => {
        const dashboardName = dashboard.name?.toLowerCase() || '';
        const lastModifiedBy = dashboard.lastModifiedBy?.toLowerCase() || '';
        const createdBy = dashboard.createdBy?.toLowerCase() || '';

        return (
          dashboardName.includes(searchLower) || lastModifiedBy.includes(searchLower) || createdBy.includes(searchLower)
        );
      });
    }
  }

  private applyFiltersAndSort(): void {
    this.filterDashboards();
    this.sortCharts();
    this.total = this.filteredDashboards.length;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.applyFiltersAndSort();
  }

  toggleSortDirection(): void {
    this.sortDescending = !this.sortDescending;
    this.sortCharts();
  }

  onMenuAction(event: any): void {
    const { action, data: dashboard } = event;
    switch (action) {
      case 'view':
        break;
      case 'edit':
        this.router.navigate(['/bi/edit-dashboard', dashboard.dashboardId]);
        break;
      case 'export': {
        const pdfUrl = `${this.appService.domainUrl}/images/dashboard/${dashboard.engagement}/${dashboard.dashboardId}/dashboard.pdf?v=${dashboard.lastModifiedAt}`;
        this.s3Service.download(pdfUrl, `${dashboard.name}.pdf`);
        break;
      }
      case 'delete':
        this.modalService.confirm({
          nzTitle: `Are you sure you want to delete this dashboard?`,
          nzContent:
            'Deleting this dashboard will permanently remove it from your workspace. This action cannot be undone.',
          nzOkText: `Yes, delete`,
          nzOnOk: () => this.deleteDashboard(dashboard),
          nzCancelText: 'Cancel',
        });
        break;
    }
  }

  trackByDashboardId(index: number, dashboard: DashboardMetadata): string {
    return dashboard.dashboardId;
  }

  get paginatedDashboards(): DashboardMetadata[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.filteredDashboards.slice(startIndex, endIndex);
  }

  private deleteDashboard(dashboard: DashboardMetadata) {
    this.isLoading = true;
    this.spinnerContent = 'Deleting dashboard...';
    const deleteInput: DeleteChartOrDashboardInput = {
      engagementId: dashboard.engagement,
      entityId: dashboard.dashboardId,
      entityType: EntityType.DASHBOARDS,
    };
    this.apiService
      .query<DeleteChartsOrDashboardsMutation, DeleteChartsOrDashboardsMutationVariables>(DeleteChartsOrDashboards, {
        input: deleteInput,
      })
      .then((response) => {
        const deleteResponse = response.data?.DeleteChartsOrDashboards as DeleteResponse;
        if (deleteResponse.statusCode !== 200) {
          this.nzMessageService.error(`Failed to delete dashboard: ${deleteResponse.message}`);
        } else {
          this.nzMessageService.success(`Dashboard successfully deleted`);
          this.loadDashboards();
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to delete dashboard: ${error}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}
